import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {Checkbox, CircularProgress, FormControlLabel, FormLabel, Stack, Switch} from '@mui/material';
import {CoreDataTextField} from '../../../../components/core/textFieldWithCoreData';
import _ from 'lodash';
import moment from 'moment';
import {ControlInvestigations, ControlTreatment} from '../../../../controls/agyle/diagnosis';
import Typography from '@mui/material/Typography';
import {TextFieldDebounce} from '../../../../controls/agyle/textFieldDebounce';
import {
    ControlDependency,
    ControlDiscriminator,
    ControlPresentation,
    useStyles,
} from '../../../../controls/agyle/triageControls';
import API from '../../../../app/api';
import {addObservationsToExaminationInput, mapObservationFromAPI} from '../observation/obsUtils';
import InjuryForm from '../injury/injuryForm';
import {useDispatch, useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import {ChiefOfComplaint, Safeguarding} from '../../../../controls/agyle/ecds';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import MultipleEntriesFormList from '../../../../components/forms/multipleEntriesFormList';
import ObservationTable from '../../../../components/observation/observationTable';
import uuidv4 from 'uuid/v4';
import PainScore from '../../../../components/observation/painScore';
import Formatter from '../../../../util/formatter';
import {chiefComplaint, getEcdsVersion} from '../../../../store/reducers/dicts';
import InformationBox from '../../../../components/other/informationBox';
import InputAdornment from '@mui/material/InputAdornment';
import useCliniciansAndNursesDict from '../../../../hooks/useCliniciansAndNursesDict';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import useStickyState from '../../../../hooks/useStickyState';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import Divider from '@mui/material/Divider';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import MessageBox from '../../../../util/messageBox';
import useConsultantsDict from '../../../../hooks/useConsultantsDict';
import {consultantDictionary, getConsultantName} from '../../../../controls/agyle/clinitian';
import {getPatientFlag, getPatientFlagValue, isFlagDisabled} from '../../mainScreen/util/patientFlagsUtil';
import {renderDiscriminationDescription, triageBase} from './triageUtils';
import {getConfig} from '../../../../util/configUtil';
import {PATIENT_AGE_TYPE} from '../../../../util/patientContextUtil';
import {openPatientForm} from '../../../../store/reducers/patientContext';
import {obsIdToFormAdd, setObsFromForms, setObsIdToFormAdd} from '../../../../store/reducers/formsAdditionalHelper';

export const unitOptions = [
    {id: 'kg', value: 'kg', name: 'Metric'},
    {id: 'st', value: 'st', name: 'Imperial Stones'},
    {id: 'lb', value: 'lb', name: 'Imperial Pounds'},
];

const Triage = (props) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);
    const [isDark, setIsDark] = useState(null);

    const obsIdToSave = useSelector(obsIdToFormAdd);

    const controller = new AbortController();
    const dictChiefComplaint = useSelector(chiefComplaint);
    const version = dispatch(getEcdsVersion(props.patient?.admission?.startDate));

    // Observation
    const [obs, setObs] = React.useState(null);
    const [obsLoading, setObsLoading] = React.useState(false);
    const [newerObs, setNewerObs] = React.useState({
        status: false,
        date: null,
    });
    const [isOpenObsDialog, openObsDialog, closeObsDialog] = useOpenState(false);
    const [chosenObs, setChosenObs] = useState([]);
    const [isOneByOne, setIsOneByOne] = useState(false);
    const [oneByOne, setOneByOne] = useStickyState(false, 'agyle_obsSideBySide');

    // Uniq id for CoreDS
    const [historicPresentingComplaintId] = useState(() => uuidv4() + '');
    const [pastMedicalHistoryId] = useState(() => uuidv4() + '');
    const [medicationsId] = useState(() => uuidv4() + '');
    const [socialHistoryId] = useState(() => uuidv4() + '');
    const [allergiesAndReactionsId] = useState(() => uuidv4() + '');

    // Flags
    const [flags, setFlags] = React.useState({
        fitToSit: getPatientFlagValue(props.patient, 'fitToSit'),
        seeAndTreat: getPatientFlagValue(props.patient, 'seeAndTreat'),
        inCar: getPatientFlagValue(props.patient, 'inCar'),
        flag111: getPatientFlag(props.patient, '111'),
        res: getPatientFlagValue(props.patient, 'res'),
        maj: getPatientFlagValue(props.patient, 'maj'),
        amb: getPatientFlagValue(props.patient, 'amb'),
        min: getPatientFlagValue(props.patient, 'min'),
    });
    const [flag111Date, setFlag111Date] = React.useState(flags?.flag111?.when111 || moment().toISOString());

    // Configs
    const dateTimeFormat = getConfig('defaultDateTimeFormat');
    const escortInTriage = getConfig('escortInTriage', false);
    const disableTriageSafeguardingConcernsAutoCheck = getConfig('disableTriageSafeguardingConcernsAutoCheck', false);
    const consultantComponentEnabled = getConfig('consultantComponentEnabled', false);
    const triageObservationMandatory = getConfig('triageObservationMandatory', false);
    const customLabelPresentation = getConfig('modules.customLabels.triage.presentation');
    const allergiesNKDA = getConfig('allergiesNKDA', false);
    const allergiesMandatory = getConfig('allergiesMandatory', false);
    const allergiesUnknown = getConfig('allergiesUnknown', false);
    const alternativeWorkStreamTriage = getConfig('alternativeWorkstreamTriage', false);
    const disableTriageDefaultRoutineCare = getConfig('disableTriageDefaultRoutineCare', false);
    const priorAnalgesiaInTriage = getConfig('priorAnalgesiaInTriage', false);
    const triageAnalgesiaPriorTextChange = getConfig('triageAnalgesiaPriorTextChange', false);
    const disableTriageExamination = getConfig('disableTriageExamination', false);
    const copyObsButtonsAvailable = getConfig('copyObsButtonsAvailable', false);
    const gpExpected = getConfig('gpExpected', false);
    const triageTreatmentForm = getConfig('triageTreatmentForm', false);
    const mandatoryTriageCC = getConfig('mandatoryTriageCC', true);

    // TODO: temporary fix
    //const [nursesCliniciansList, userFind] = config.consultantComponentEnabled ? useConsultantsDict('triage') : useCliniciansAndNursesDict("forTriage");
    const [nursesCliniciansList, userFind] = useCliniciansAndNursesDict('forTriage');
    const [cliniciansList, userFind2] = useConsultantsDict(consultantDictionary.triage);

    const firstUpdateFlag = useRef(true);

    const {isSmall} = props;

    const [expanded, setExpanded] = React.useState({
        obs: false,
        flags: false,
        information: false,
        safeguarding: false,
        investigation: false,
        managePlan: false,
        injury: false,
        escort: false,
    });

    // Escort
    const [escortList, setEscortList] = useState([]);
    const [escort, setEscort] = useState({
        id: uuidv4() + '',
        parentalResponsibility: false,
        attendedWithChild: false,
        name: '',
        age: '',
        relationship: '',
        contact: '',
    });
    const [errorEscort, setErrorEscort] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);
    const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] = useOpenState(false, true);
    const escortListUpdated = useRef(false);
    const firstUpdate = useRef(true);

    const getTriageBase = () => {
        let createdForm = _.merge({}, triageBase(), props.patient.triage);
        let chief = null;

        if ((props.patient?.admission?.initialAEComplaintDescription || props.patient?.admission?.initialAEComplaintSnomedCode) && !props.patient?.triage?.discriminator) {
            const description = props.patient?.admission?.initialAEComplaintDescription;
            const code = props.patient?.admission?.initialAEComplaintSnomedCode;

            dictChiefComplaint.forEach((el) => {
                const matchesCode = code && _.startsWith(el.snomedCode, code);
                const matchesDescription = !code && _.startsWith(el.__description, description);
                if ((matchesCode || matchesDescription) && el.ecdsVersion === version) {
                    chief = el;
                }
            });
        }

        if (chief) {
            createdForm.chiefComplaint = chief;
            createdForm.chiefComplaintAuthor = 'PAS @ ' + moment().set('second', 0).format(dateTimeFormat);
        }

        if (props.patient?.studies?.mainScreenSafeguarding) {
            if (!disableTriageSafeguardingConcernsAutoCheck) {
                createdForm.safeguardingConcerns = true;
                createdForm.safeguardingConcernsAuthor = 'Safeguarding @ ' + moment(props.patient?.studies?.paedSafeguarding?.date).format(dateTimeFormat);
            }
            createdForm.safeguarding = props.patient.studies.mainScreenSafeguarding;
        }

        if (createdForm.historicPresentingComplaint === '' && props.patient?.metadata?.historyOfPresentingComplaint) {
            createdForm.historicPresentingComplaint = props.patient?.metadata?.historyOfPresentingComplaint;
        }

        if (props.patient.triage?.['dependency']) createdForm.dependencyTriage = props.patient.triage['dependency'];

        createdForm.who = tester;
        createdForm.whoUserName = userObject.userName;
        createdForm.when = props.patient.triage?.when ? props.patient.triage.when : moment().set('second', 0).toISOString();

        return createdForm;
    };

    const setAuthor = (triage) => {
        let triageTmp = {
            ...triage,
            patientWeightUnits: triage?.patientWeightUnits ? triage.patientWeightUnits : 'kg',
        };

        triageTmp.who = tester;
        triageTmp.whoUserName = userObject.userName;
        return triageTmp;
    };

    const [triageObject, setTriageObject] = React.useState(props.patient.triage?.discriminator ? setAuthor(props.patient.triage) : getTriageBase);
    const init = useRef(true);
    const dependencyInit = useRef(false);
    const nutritionInit = useRef(false);
    const [isInjury, setIsInjury] = React.useState(triageObject.chiefComplaint ? triageObject.chiefComplaint.flagInjury : '0');

    useEffect(() => {
        setIsOneByOne(oneByOne);
        loadLastObs();

        setIsDark(window._getTheme() === 'dark');

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (!firstUpdateFlag.current) {
            props.saveFlag(flags);
        }
    }, [flags]);

    useEffect(() => {
        if (escortInTriage && !_.isNull(props.escortList) && !escortListUpdated.current) {
            setEscortList(props.escortList);
            escortListUpdated.current = true;
        }
    }, [props.escortList]);

    useEffect(() => {
        if (escortInTriage && props.setEscortList && !firstUpdate.current) {
            props.setEscortList(escortList);
        }
        firstUpdate.current = false;
    }, [escortList]);

    useEffect(() => {
        if (props.isError && allergiesNKDA && !expanded.information && !triageObject.allergies) {
            setExpanded((prev) =>
                ({
                    ...prev,
                    information: true,
                }),
            );
        }
    }, [props.isError, expanded, triageObject]);

    useEffect(() => {
        if (consultantComponentEnabled) {
            if (userFind2 && !triageObject?.triageClinicians && !props.patient?.triage?.presentation) {
                dependencyInit.current = true;
                setTriageObject(prev => ({
                    ...prev,
                    triageClinicians: userFind2,
                }));
            }
        } else {
            if (userFind && !triageObject?.triageClinicians && !props.patient?.triage?.presentation) {
                dependencyInit.current = true;
                setTriageObject(prev => ({
                    ...prev,
                    triageClinicians: userFind,
                }));
            }
        }
    }, [userFind, userFind2]);


    useEffect(() => {
        setIsInjury(triageObject.chiefComplaint ? triageObject.chiefComplaint.flagInjury : '0');
        if (triageObject) {
            props.save(triageObject);
            if (init.current || dependencyInit.current || nutritionInit.current) {
                init.current = false;
                dependencyInit.current = false;
                nutritionInit.current = false;
            } else {
                props.triageChange(true);
            }
        }
    }, [triageObject]);

    useEffect(() => {
        if (!props.patient?.triage && triageObject.patientWeight === '' && props.lastNutrition
            && moment(props.lastNutrition.date).isAfter(props.patient?.admission?.startDate)) {
            nutritionInit.current = true;
            setTriageObject(prev => ({
                ...prev,
                patientWeight: props.lastNutrition.weight,
            }));
        }
    }, [props.lastNutrition]);

    useEffect(() => {
        if (obsIdToSave) {
            setTriageObject(prevState => ({...prevState, savedObservationId: obsIdToSave}));
            loadLastObs(obsIdToSave);
            dispatch(setObsIdToFormAdd(null));
        }
    }, [obsIdToSave]);

    const copyLastObs = () => {
        const lastObs = Formatter.formatObservationToText(props.patient?.patient?.lastObservation);

        setTriageObject((prevState) => ({
            ...prevState,
            examination: prevState?.examination !== '' ? prevState.examination + '\n' + lastObs : lastObs,
        }));
    };

    const accordionChangeHandler = (accordion) => (ev) => {
        setExpanded((prev) =>
            ({
                ...prev,
                [accordion]: !prev[accordion],
            }),
        );
    };

    const InjuryCwiltedComponent = () => {
        const age = moment().diff(props.patient.patient.dob, 'years');
        return (
            <Grid item xs={12}>
                <AgyleAccordion
                    expanded={expanded.injury}
                    onChange={accordionChangeHandler('injury')}
                    label={props.patientType !== PATIENT_AGE_TYPE.PAEDS ? t('Injury') : t('CWILTED')}
                    formBackground
                >
                    <MultipleEntriesFormList
                        patient={props.patient}
                        type={'injury'}
                        title={age >= 18 ? 'Injury Data Set' : 'CWILTED Data Set'}
                        field={'studies.injury'}
                        parentForm={'Triage'}
                        onSave={props.onSave}
                        width={'lg'}
                        triage
                        disableSave={props.disableSave}
                        patientHaveModification={props.patientHaveModification}
                        modifyPatient={props.modifyPatient}
                        histPresComplaint={triageObject?.historicPresentingComplaint}
                        component={<InjuryForm isInjury={isInjury} patient={props.patient}
                                               histPresComplaint={triageObject?.historicPresentingComplaint}/>}
                    />
                </AgyleAccordion>
            </Grid>
        );
    };

    const checkboxHandler = (checked, name) => {
        setEscort(prevState => ({...prevState, [name]: checked}));
    };

    const handleChangeEscort = (event) => {
        const {name, value} = event.target;
        setEscort(prevState => ({...prevState, [name]: value}));
    };

    const handleContact = (e) => {
        const onlyNums = e.target.value.replace(/[^\d\-]/g, '');

        setEscort(prevState => ({...prevState, contact: onlyNums}));
    };

    const addToTable = () => {
        const valid = escort.attendedWithChild ? escort.name !== '' && escort.relationship !== '' : escort.name !== '' && escort.relationship !== '' && escort.contact !== '';
        if (!valid) {
            setErrorEscort(true);
        } else {
            setEscortList(prevState => [...prevState, escort]);

            // Clear fields after add to table
            setEscort({
                id: uuidv4() + '',
                parentalResponsibility: false,
                attendedWithChild: false,
                name: '',
                age: '',
                relationship: '',
                contact: '',
            });
            setErrorEscort(false);
        }
    };

    const renderInfo = () => {
        const parents = _.find(escortList, {parentalResponsibility: true});
        const attended = _.find(escortList, {attendedWithChild: true});
        let message = '';

        if (!parents && !attended) {
            message = 'Please add who does have parental/legal responsibility for the patient and what are their contact details, and who attended with a child';
        } else if (!parents && attended) {
            message = 'Please add who does have parental/legal responsibility for the patient and what are their contact details';
        } else if (parents && !attended) {
            message = 'Please add who attended with a child';
        } else {
            return;
        }

        return (
            <InformationBox type="info" style={{margin: '5px 0'}}>
                {t(message)}
            </InformationBox>
        );
    };

    const handleDeleteRecord = (row) => {
        setDeleteItem(row);
        openConfirmDelete();
    };

    const deleteReferral = () => {
        setEscortList(_.filter(escortList, escort => escort.id !== deleteItem.id));
        setDeleteItem(null);
        closeConfirmDelete();
    };

    const renderTable = () => {
        const columns = [
            createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('ptName', 'Person Name', 2, 'name', 'String', '', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('responsibility', 'Parental Responsibility', 3, 'parentalResponsibility', 'boolean', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('attendedWithChild', 'Attended With a Child', 4, 'attendedWithChild', 'boolean', '', 'medium', 'left', true, true, null, null, null),
            createAgyleTableColumn('age', 'Age', 5, 'age', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('relationship', 'Relationship', 6, 'relationship', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('contact', 'Contact Details', 7, 'contact', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('delete', 'Action', 8, null, 'action', '', 'medium', 'right', true, false, null, null, null),
        ];

        if (!_.isEmpty(escortList)) {
            return (
                <div style={{marginBottom: 10}}>
                    <AgyleTable
                        columns={columns}
                        data={escortList}
                        deleteItem={handleDeleteRecord}
                    />
                </div>
            );
        }
    };

    const renderEscortComponent = () => {
        return (
            <>
                <AgyleAccordion
                    expanded={expanded.escort}
                    onChange={accordionChangeHandler('escort')}
                    label={t('Escort')}
                    formBackground
                >
                    <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item xs={12}>
                            {renderInfo()}
                            {renderTable()}
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <div className={classes.divRow}>
                                <AgyleBlockSwitch
                                    value={escort.parentalResponsibility}
                                    onChange={event => checkboxHandler(event, 'parentalResponsibility')}
                                />

                                <FormLabel sx={{ml: 1}}>
                                    {t('Parental Responsibility')}
                                </FormLabel>
                            </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <AgyleTextField
                                name="name"
                                label={t('Name')}
                                onChange={handleChangeEscort}
                                value={escort.name}
                                error={errorEscort}
                                debounce
                                size="medium"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} md={1}>
                            <AgyleTextField
                                name="age"
                                label={t('Age')}
                                type="number"
                                onChange={handleChangeEscort}
                                value={escort.age}
                                maxLength={3}
                                debounce
                                size="medium"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <AgyleTextField
                                name="relationship"
                                label={t('Relationship')}
                                onChange={handleChangeEscort}
                                value={escort.relationship}
                                error={errorEscort}
                                debounce
                                size="medium"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <div className={classes.divRow}>
                                <AgyleBlockSwitch
                                    value={escort.attendedWithChild}
                                    onChange={event => checkboxHandler(event, 'attendedWithChild')}
                                />

                                <FormLabel sx={{ml: 1}}>
                                    {t('Attended with a child')}
                                </FormLabel>
                            </div>
                        </Grid>

                        <Grid item xs={12} md={9}>
                            <AgyleTextField
                                name="contact"
                                label={t('Contact details')}
                                onChange={handleContact}
                                value={escort.contact}
                                error={(errorEscort && !escort.attendedWithChild)}
                                debounce
                                size="medium"
                                fullWidth
                            />
                        </Grid>

                        <div style={{marginLeft: 'auto', marginTop: 10}}>
                            <AgyleButton
                                variant="contained"
                                onClick={addToTable}
                                startIcon={<PersonAddIcon/>}
                            >
                                {t('Add')}
                            </AgyleButton>
                        </div>
                    </Grid>
                </AgyleAccordion>

                <MessageBox
                    onClose={closeConfirmDelete}
                    title={t('Confirmation')}
                    message={t('Do you really want to remove this item?')}
                    haveAccept={{yes: t('Submit'), no: t('Close')}}
                    onAccept={deleteReferral}
                    open={isConfirmDeleteOpen}
                />
            </>
        );
    };

    const lazyChangeFunction = (field, event) => {
        setTriageObject((pr) =>
            ({
                ...pr,
                [field]: event.target.value,
                [field + 'Author']: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
            }),
        );
    };

    const setAllergies = (type) => {
        setTriageObject((pr) =>
            ({
                ...pr,
                allergies: type,
            }),
        );
    };

    const loadLastObs = (id) => {
        if (!props.modeClerking) {
            setObsLoading(true);
            API.getFullObservations(props.patient, props.patientAgeType === PATIENT_AGE_TYPE.PAEDS ? 'pews' : 'news', true, controller.signal)
                .then((result) => {
                const savedId = id || triageObject.savedObservationId;

                const savedObs = _.filter(result, {__id: savedId});
                let filteredObs = _.orderBy(result, ['examinationStart'], ['desc']);
                let lastObs;

                if (!_.isEmpty(filteredObs)) {
                    filteredObs = _.filter(filteredObs, element => {
                        return ((element.status === 'completed' || element.status === 'notRecorded') && (element.investigation === 'pews' || element.investigation === 'news'));
                    });
                    lastObs = !_.isEmpty(filteredObs) ? mapObservationFromAPI(filteredObs[0]) : null;
                }

                let lastObsData = {
                    date: lastObs ? lastObs.startedDate : null,
                };
                if (!_.isEmpty(savedObs)) {
                    setObs(mapObservationFromAPI(savedObs[0]));
                    lastObsData = {...lastObsData, status: lastObs?.startedDate > savedObs.startedDate};
                    setNewerObs(lastObsData);
                } else {
                    lastObsData = {...lastObsData, status: !_.isEmpty(filteredObs)};
                    setNewerObs(lastObsData);
                }
            }).finally(() => {
                setObsLoading(false);
            });
        }
    };

    const setOptional = (flags, flag, value) => {
        let data = {
            inCar: flags.inCar,
            maj: flags.maj,
            res: flags.res,
            amb: flags.amb,
            min: flags.min,
        };
        if (value) {
            data.inCar = data.maj = data.res = data.amb = data.min = false;
        }
        data[flag] = value;
        return data;
    };

    const handleChangesFlags = (event, name) => {
        const checked = event.target.value === 'true';

        firstUpdateFlag.current = false;
        let data;

        // eslint-disable-next-line default-case
        switch (name) {
            case 'fitToSit': {
                data = {
                    fitToSit: checked,
                    seeAndTreat: checked ? false : flags.seeAndTreat,
                };
                setFlags(prevState => ({...prevState, ...data}));
                break;
            }
            case 'seeAndTreat': {
                data = {
                    fitToSit: checked ? false : flags.fitToSit,
                    seeAndTreat: checked,
                };
                setFlags(prevState => ({...prevState, ...data}));
                break;
            }
            case 'inCar':
            case 'maj':
            case 'res':
            case 'amb':
            case 'min': {
                data = setOptional(flags, name, checked);
                setFlags(prevState => ({...prevState, ...data}));
                break;
            }
            case '111' : {
                if (!checked) {
                    setFlag111Date(moment().toISOString());
                }
                data = {
                    checked: checked,
                    when111: !checked ? null : (event.target.date111 ? event.target.date111 : flag111Date),
                };
                setFlags(prevState => ({...prevState, flag111: {...data}}));
            }
        }
    };

    const handleChangesFlagsDate = (event, s) => {
        setFlag111Date(moment(event).toISOString());
        handleChangesFlags({
            target: {
                name: s,
                checked: flags.flag111 && flags.flag111.checked ? flags.flag111.checked : false,
                date111: moment(event).toISOString(),
            },
        });
    };

    const addObsAndClose = () => {
        closeObsDialog();
        let note = '';

        if (isOneByOne) {
            addObservationsToExaminationInput(chosenObs, setChosenObs, setTriageObject, 'examination');
        } else {
            chosenObs.forEach(obsNote => {
                const tempNote = Formatter.formatObservationToText(obsNote);
                note = note !== '' ? note + '\n\n' + tempNote : tempNote;
            });

            setTriageObject(prevState => ({
                ...prevState,
                examination: prevState.examination !== '' ? prevState.examination + '\n\n' + note : note,
            }));

        }
    };

    const handleObsModeChange = () => {
        setOneByOne(prev => !prev);
        setIsOneByOne(prev => !prev);
    };

    const handleOpenObs = () => {
        dispatch(setObsFromForms(true));
        dispatch(openPatientForm('obs'));
    };

    const renderObs = () => {
        return (
            <>
                {triageObject.observationNotRequired &&
                    <Grid item xs={12} className={classes.infoObs}>
                        {t('"OBS not required" action was registered in this triage. To be able to add observation uncheck "OBS not required"')}
                    </Grid>
                }

                <Grid container spacing={2} alignItems="center" sx={{mt: 1, mb: 2}}>
                    {obsLoading &&
                        <Grid item xs={12}>
                            <div className={classes.loading}>
                                <p>{t('Loading Observation data. Please wait.')}</p>
                                <CircularProgress/>
                            </div>
                        </Grid>
                    }

                    {(!obsLoading && (newerObs && newerObs.status)) &&
                        <Grid item xs={12}>
                            <InformationBox type="info">
                                {t('Last observations recorded at ')}{moment(newerObs.date).format(dateTimeFormat)}
                            </InformationBox>
                        </Grid>
                    }

                    {!obs &&
                        <>
                            <Grid item xs={isSmall ? 12 : 6}>
                                <AgyleButton
                                    width="100%"
                                    variant="contained"
                                    disabled={props.isFormDisabled || obsLoading || triageObject.observationNotRequired}
                                    className={classes.fullWidth}
                                    onClick={handleOpenObs}
                                >
                                    {t('Add observation')}
                                </AgyleButton>
                            </Grid>

                            <Grid item xs={isSmall ? 12 : 6}>
                                <AgyleButton
                                    variant="contained"
                                    disabled={obsLoading || !props.canEditTriage}
                                    style={{width: '100%'}}
                                    color={(props.isError && (triageObservationMandatory && !triageObject?.observationNotRequired && !triageObject?.savedObservationId)) ? 'error' : 'primary'}
                                    onClick={() => setTriageObject(prevState =>
                                        ({
                                            ...prevState,
                                            observationNotRequired: !prevState.observationNotRequired,
                                            observationNotRequiredTimestamp: moment().toISOString(),
                                        }))}
                                >
                                    {t('Observation not required')}
                                </AgyleButton>
                            </Grid>
                        </>
                    }

                    {!!obs &&
                        <>
                            <Grid item xs={12} sx={{mt: 2}}>
                                <Divider/>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    {t('Observation completed during triage')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <ObservationTable
                                    specificObs={obs}
                                    patient={props.patient}
                                />
                            </Grid>
                        </>
                    }

                    <Grid item xs={12} justifyContent={'center'}>
                        <PainScore
                            patient={props.patient}
                            value={triageObject.painScore}
                            obsId={triageObject.painScoreId}
                            disabled={!props.canEditTriage}
                            onChange={(value) =>
                                setTriageObject((prevState) => ({
                                    ...prevState,
                                    painScore: value,
                                }))
                            }
                            onSaveObs={(data) => {
                                if (data.success) setTriageObject((prevState) => ({
                                    ...prevState,
                                    painScoreId: data.obsId,
                                }));
                            }}
                        />
                    </Grid>
                </Grid>
            </>
        );
    };

    const renderManchesterTriage = () => {
        return (
            <>
                <Grid container direction="row" spacing={2} sx={{my: 2}}>
                    <Grid item xs={isSmall ? 12 : 6}>
                        <ControlPresentation
                            label={t(customLabelPresentation || 'Presentation')}
                            value={triageObject.presentation}
                            disabled={!props.canEditTriage}
                            agyleRefresh
                            discriminator={triageObject.discriminator}
                            error={!triageObject.presentation && props.isError}
                            onChange={value => {
                                setTriageObject((pr) =>
                                    ({
                                        ...pr,
                                        presentation: value,
                                        discriminator: null,
                                    }),
                                );
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={isSmall ? 12 : 6}>
                        <ControlDiscriminator
                            label={t('Discriminator')}
                            value={triageObject.discriminator}
                            agyleRefresh
                            presentation={triageObject.presentation}
                            disabled={!props.canEditTriage}
                            error={!triageObject.discriminator && props.isError}
                            onChange={value => {
                                setTriageObject((pr) =>
                                    ({...pr, discriminator: value}));
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                </Grid>
                {triageObject?.discriminator?.details && renderDiscriminationDescription(triageObject, isDark)}
            </>
        );
    };

    const renderInformationAccordion = () => {
        return (
            <Grid item xs={12} sx={{mt: 2}}>
                <AgyleAccordion
                    expanded={expanded.information}
                    onChange={accordionChangeHandler('information')}
                    label={t('Information')}
                    formBackground
                >
                    <Grid container spacing={2}>
                        {props.patientType === PATIENT_AGE_TYPE.PAEDS && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <AgyleTextField
                                        disabled={!props.canEditTriage}
                                        style={{width: '100%'}}
                                        label={t('Patient Weight')}
                                        type="number"
                                        onChange={event => {
                                            const {value} = event.target;
                                            if (!_.isNaN(value)) {
                                                setTriageObject((pr) =>
                                                    ({
                                                        ...pr,
                                                        patientWeight: value,
                                                    }));
                                            }
                                        }}
                                        value={triageObject.patientWeight}
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        endAdornment={<InputAdornment
                                            position="end">{triageObject.patientWeightUnits}</InputAdornment>}
                                        InputProps={{
                                            endAdornment: <InputAdornment
                                                position="end">{triageObject.patientWeightUnits}</InputAdornment>,
                                        }}
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <AgyleSelectField
                                        label={t('Unit')}
                                        style={{height: 54}}
                                        onChange={(value) => {
                                            setTriageObject((pr) =>
                                                ({
                                                    ...pr,
                                                    patientWeightUnits: value,
                                                }));
                                        }}
                                        withBackground
                                        options={unitOptions}
                                        value={triageObject?.patientWeightUnits || 'kg'}
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <CoreDataTextField
                                disabled={!props.canEditTriage}
                                agyleRefresh
                                patient={props.patient.patient.__id}
                                path="core.patient.pastMedicalHistory"
                                instanceId={pastMedicalHistoryId}
                                label={t('Past Medical History')}
                                multiline
                                variant="outlined"
                                value={triageObject.pastMedicalHistory}
                                onChange={e => lazyChangeFunction('pastMedicalHistory', e)}
                                showCoreValue
                                height={70}
                                hideHistory={props.pastAttendances}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CoreDataTextField
                                disabled={!props.canEditTriage}
                                patient={props.patient.patient.__id}
                                agyleRefresh
                                path="core.patient.allergies"
                                instanceId={allergiesAndReactionsId}
                                label={t('Allergies and reactions')}
                                variant="outlined"
                                multiline
                                value={triageObject.allergies}
                                onChange={e => lazyChangeFunction('allergies', e)}
                                showCoreValue
                                height={70}
                                error={allergiesMandatory && !triageObject.allergies && props.isError}
                                hideHistory={props.pastAttendances}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12} className={classes.nkdaButton}>
                            {allergiesNKDA &&
                                <AgyleButton
                                    color="primary"
                                    variant="contained"
                                    onClick={setAllergies.bind(this, 'NKDA')}
                                >
                                    {t('NKDA')}
                                </AgyleButton>
                            }

                            {allergiesUnknown &&
                                <AgyleButton
                                    color="primary"
                                    variant="contained"
                                    onClick={setAllergies.bind(this, 'Unknown')}
                                >
                                    {t('Unknown')}
                                </AgyleButton>
                            }
                        </Grid>

                        <Grid item xs={12}>
                            <CoreDataTextField
                                disabled={!props.canEditTriage}
                                patient={props.patient.patient.__id}
                                path="core.patient.medications"
                                instanceId={medicationsId}
                                label={t('Medications')}
                                multiline
                                agyleRefresh
                                variant="outlined"
                                value={triageObject.medications}
                                onChange={e => lazyChangeFunction('medications', e)}
                                showCoreValue
                                height={70}
                                hideHistory={props.pastAttendances}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CoreDataTextField
                                disabled={!props.canEditTriage}
                                patient={props.patient.patient.__id}
                                path="core.patient.socialHistory"
                                instanceId={socialHistoryId}
                                label={t('Social History')}
                                multiline
                                agyleRefresh
                                variant="outlined"
                                value={triageObject.socialHistory}
                                onChange={e => lazyChangeFunction('socialHistory', e)}
                                showCoreValue
                                height={70}
                                hideHistory={props.pastAttendances}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    </Grid>
                </AgyleAccordion>
            </Grid>
        );
    };

    const renderSafeguarding = () => {
        return (
            <Grid item xs={12} sx={{mt: 2}}>
                <AgyleAccordion
                    expanded={expanded.safeguarding}
                    onChange={accordionChangeHandler('safeguarding')}
                    label={t('Safeguarding')}
                    formBackground
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={!props.canEditTriage}
                                        onChange={value => {
                                            setTriageObject((pr) =>
                                                ({
                                                    ...pr,
                                                    safeguardingConcerns: value.target.checked,
                                                    safeguardingConcernsDetails: value.target.checked ? pr.safeguardingConcernsDetails : '',
                                                    safeguardingConcernsAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                }),
                                            );
                                        }}
                                        checked={triageObject.safeguardingConcerns}
                                        inputProps={{'aria-label': 'primary checkbox'}}
                                    />
                                }
                                label="Safeguarding Concerns"
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            {triageObject.safeguardingConcerns && props.patientType !== PATIENT_AGE_TYPE.PAEDS &&
                                <div>
                                    {t('Please consider completing form Cause for concern (Safeguarding Adults) Referral')}
                                </div>
                            }
                        </Grid>

                        {triageObject.safeguardingConcerns &&
                            <Grid item xs={12}>
                                <Safeguarding
                                    label={t('Safeguarding')}
                                    value={triageObject.safeguarding}
                                    limitTags={4}
                                    agyleRefresh
                                    color={'primary'}
                                    onChange={event => {
                                        setTriageObject((pr) =>
                                            ({
                                                ...pr,
                                                safeguarding: event,
                                            }),
                                        );
                                    }}
                                    admitDate={props.patient?.admission?.startDate}
                                />
                            </Grid>
                        }

                        {props.patientType === PATIENT_AGE_TYPE.OVER75 && triageObject.safeguardingConcerns &&
                            <Grid item xs={12}>
                                <TextFieldDebounce
                                    style={{width: '100%'}}
                                    label={t('Concerns & Actions taken')}
                                    disabled={!props.canEditTriage}
                                    multiline
                                    agyleRefresh
                                    onChange={event => {
                                        setTriageObject((pr) =>
                                            ({
                                                ...pr,
                                                safeguardingConcernsDetails: pr.safeguardingConcerns ? event.target.value : '',
                                                safeguardingConcernsAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                            }),
                                        );
                                    }}
                                    value={triageObject.safeguardingConcernsDetails}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                        }

                        {props.patientType !== PATIENT_AGE_TYPE.PAEDS &&
                            <Fragment>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled={!props.canEditTriage}
                                                onChange={value => {
                                                    setTriageObject((pr) =>
                                                        ({
                                                            ...pr,
                                                            childProtectionConcerns: value.target.checked,
                                                            childConcernsDetails: value.target.checked ? pr.childConcernsDetails : '',
                                                            childProtectionConcernsAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                        }),
                                                    );
                                                }}
                                                checked={triageObject.childProtectionConcerns}
                                                inputProps={{'aria-label': 'primary checkbox'}}
                                            />
                                        }
                                        label="Child Protection Concerns"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    {triageObject.childProtectionConcerns &&
                                        <TextFieldDebounce
                                            style={{width: '100%'}}
                                            disabled={!props.canEditTriage}
                                            label={t('Concerns & Actions taken')}
                                            multiline
                                            agyleRefresh
                                            onChange={event => {
                                                setTriageObject((pr) =>
                                                    ({
                                                        ...pr,
                                                        childConcernsDetails: pr.childProtectionConcerns ? event.target.value : '',
                                                        childProtectionConcernsAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                                    }),
                                                );
                                            }}
                                            value={triageObject.childConcernsDetails}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    }
                                </Grid>
                            </Fragment>
                        }
                    </Grid>
                </AgyleAccordion>
            </Grid>
        );
    };

    return (
        triageObject &&
        <>
            <Grid container direction="row" spacing={2} sx={{mt: 0}}>
                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDictionaryField
                        value={triageObject.triageClinicians || null}
                        label={t('Triage Clinicians')}
                        disabled={!props.canEditTriage}
                        withBackground
                        getOptionLabel={option => getConsultantName(option)}
                        options={consultantComponentEnabled ? cliniciansList : nursesCliniciansList}
                        groupBy={opt => opt.group}
                        isOptionEqualToValue={(option, value) => option.__id == value.__id}
                        onChange={(event, newValue) => setTriageObject(prevState => ({
                            ...prevState,
                            triageClinicians: newValue,
                        }))}
                        size="large"
                        noBorder={false}
                        withNewBackground
                        error={!triageObject.triageClinicians && props.isError}
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        label={t('Date and Time of Triage')}
                        value={moment(triageObject.when)}
                        disableToolbar
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        disabled={!props.canEditTriage}
                        onError={props.pickerError}
                        onChange={value => {
                            if (value)
                                setTriageObject((pr) =>
                                    ({...pr, when: value.toISOString()}));
                        }}
                        InputProps={{classes: {root: classes.inputLabel}}}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
            </Grid>

            {props.fullFormMode &&
                <>
                    <Grid item xs={12} sx={{mt: 2}}>
                        <Divider/>
                    </Grid>

                    {!alternativeWorkStreamTriage &&
                        renderManchesterTriage()
                    }
                    <Grid container spacing={2} sx={{mt: 2}}>
                        <Grid item xs={isSmall ? 12 : 6}>
                            <ControlDependency
                                setDefault={!disableTriageDefaultRoutineCare}
                                agyleRefresh
                                label={t('Dependency')}
                                value={triageObject.dependencyTriage}
                                disabled={!props.canEditTriage}
                                onChange={(value, trigger) => {
                                    dependencyInit.current = trigger;
                                    setTriageObject((pr) =>
                                        ({...pr, dependency: value, dependencyTriage: value}));
                                }}
                                error={!triageObject.dependencyTriage && props.isError}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <ChiefOfComplaint
                                agyleRefresh
                                value={triageObject.chiefComplaint}
                                label={t('Chief Complaint')}
                                disabled={!props.canEditTriage}
                                onChange={value => {
                                    setTriageObject((pr) =>
                                        ({
                                            ...pr,
                                            chiefComplaint: value,
                                            chiefComplaintAuthor: pr.who + ' @ ' + moment().format('DD/MM/YYYY HH:mm:ss'),
                                        }),
                                    );
                                }}
                                admitDate={props.patient?.admission?.startDate}
                                size="large"
                                noBorder={false}
                                withNewBackground
                                error={mandatoryTriageCC ? !triageObject.chiefComplaint && props.isError : null}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CoreDataTextField
                                agyleRefresh
                                patient={props.patient.patient.__id}
                                path="core.patient.historicPresentingComplaint"
                                instanceId={historicPresentingComplaintId}
                                label={t('History of Presenting Complaint')}
                                variant="outlined"
                                value={triageObject.historicPresentingComplaint}
                                onChange={e => lazyChangeFunction('historicPresentingComplaint', e)}
                                showCoreValue
                                multiline
                                disabled={!props.canEditTriage}
                                height={70}
                                hideHistory={props.pastAttendances}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    </Grid>

                    {alternativeWorkStreamTriage &&
                        renderInformationAccordion()
                    }

                    {(props.patientType !== PATIENT_AGE_TYPE.PAEDS && alternativeWorkStreamTriage) &&
                        renderSafeguarding()
                    }

                    {alternativeWorkStreamTriage &&
                        renderManchesterTriage()
                    }

                    {renderObs()}

                    {(escortInTriage && props.patientType === PATIENT_AGE_TYPE.PAEDS) &&
                        <Grid item xs={12} sx={{my: 2}}>
                            {renderEscortComponent()}
                        </Grid>
                    }

                    {((triageObject && triageObject.chiefComplaint && triageObject.chiefComplaint.flagInjury === '1') || (props.patientType === PATIENT_AGE_TYPE.PAEDS)) &&
                        <Grid item xs={12} sx={{my: 2}}>
                            {InjuryCwiltedComponent()}
                        </Grid>
                    }

                    <Grid item xs={12} sx={{mt: 2}}>
                        <Divider/>
                    </Grid>

                    <Grid container direction="row" spacing={2} sx={{my: 1}}>
                        {triageObject.analgesiaSAT &&
                            <>
                                <Grid item xs={isSmall ? 12 : 6} sx={{mb: 1}}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled
                                                checked={triageObject.analgesiaSAT}
                                                inputProps={{'aria-label': 'primary checkbox'}}
                                            />
                                        }
                                        label="Analgesia given at S&T"
                                    />
                                </Grid>

                                <Grid item xs={isSmall ? 12 : 6}>
                                    <TextFieldDebounce
                                        style={{width: '100%'}}
                                        label={t('S&T Analgesia Details')}
                                        multiline
                                        agyleRefresh
                                        value={triageObject.analgesiaSATDetails}
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        size="small"
                                        inputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Grid>
                            </>
                        }

                        <Grid item xs={isSmall ? 12 : 6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={value => {
                                            setTriageObject((pr) =>
                                                ({
                                                    ...pr,
                                                    analgesia: value.target.checked,
                                                    analgesiaDetails: value.target.checked ? pr.analgesiaDetails : '',
                                                }),
                                            );
                                        }}
                                        disabled={!props.canEditTriage || triageObject.analgesiaRefused}
                                        checked={triageObject.analgesia}
                                        inputProps={{'aria-label': 'primary checkbox'}}
                                    />
                                }
                                label="Analgesia given at triage"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={value => {
                                            setTriageObject((pr) =>
                                                ({
                                                    ...pr,
                                                    analgesiaRefused: value.target.checked,
                                                    analgesiaDetails: !value.target.checked ? pr.analgesiaDetails : '',
                                                }),
                                            );
                                        }}
                                        disabled={!props.canEditTriage || triageObject.analgesia}
                                        checked={triageObject.analgesiaRefused}
                                        inputProps={{'aria-label': 'primary checkbox'}}
                                    />
                                }
                                label="Analgesia refused"
                            />
                        </Grid>

                        {triageObject.analgesia &&
                            <Grid item xs={12}>
                                <TextFieldDebounce
                                    style={{width: '100%'}}
                                    label={t('Analgesia Details')}
                                    disabled={!props.canEditTriage}
                                    multiline
                                    agyleRefresh
                                    onChange={event => {
                                        setTriageObject((pr) =>
                                            ({
                                                ...pr,
                                                analgesiaDetails: pr.analgesia ? event.target.value : '',
                                            }),
                                        );
                                    }}
                                    value={triageObject.analgesiaDetails}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                        }

                        {priorAnalgesiaInTriage && <Grid item xs={isSmall ? 12 : 6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={value => {
                                            setTriageObject((pr) =>
                                                ({
                                                    ...pr,
                                                    analgesiaPrior: value.target.checked,
                                                    analgesiaPriorDetails: value.target.checked ? pr.analgesiaPriorDetails : '',
                                                }),
                                            );
                                        }}
                                        disabled={!props.canEditTriage}
                                        checked={triageObject.analgesiaPrior}
                                        inputProps={{'aria-label': 'primary checkbox'}}
                                    />
                                }
                                label={t(!triageAnalgesiaPriorTextChange
                                    ? 'Patient took analgesia prior to hospital'
                                    : 'Medications before triage',
                                )}
                            />
                        </Grid>
                        }

                        <Grid item xs={isSmall ? 12 : 6}>
                            {triageObject.analgesiaPrior &&
                                <TextFieldDebounce
                                    style={{width: '100%'}}
                                    label={t(!triageAnalgesiaPriorTextChange
                                        ? 'Analgesia taken prior to the hospital'
                                        : 'Medications before triage',
                                    )}
                                    disabled={!props.canEditTriage}
                                    multiline
                                    agyleRefresh
                                    onChange={event => {
                                        setTriageObject((pr) =>
                                            ({
                                                ...pr,
                                                analgesiaPriorDetails: pr.analgesiaPrior ? event.target.value : '',
                                            }),
                                        );
                                    }}
                                    value={triageObject.analgesiaPriorDetails}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            }
                        </Grid>
                    </Grid>

                    {!disableTriageExamination &&
                        <Grid item xs={12} sx={{mt: 3}}>
                            <TextFieldDebounce
                                disabled={props.isFormDisabled}
                                style={{width: '100%'}}
                                maxRows={25}
                                label={t('Examination')}
                                multiline
                                agyleRefresh
                                onChange={(event) => {
                                    setTriageObject((pr) => ({
                                        ...pr,
                                        examination: event.target.value,
                                    }));
                                }}
                                value={triageObject?.examination}
                                variant="outlined"
                                changeValueTrigger
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            <div style={{display: 'flex'}}>
                                {copyObsButtonsAvailable &&
                                    <AgyleButton
                                        onClick={copyLastObs}
                                        disabled={!props.patient?.patient?.lastObservation || props.isFormDisabled}
                                        style={{marginTop: 10}}
                                        variant="contained"
                                    >
                                        {t('Copy Last Observation')}
                                    </AgyleButton>

                                }
                                {copyObsButtonsAvailable &&
                                    <AgyleButton
                                        onClick={openObsDialog}
                                        disabled={!props.patient?.patient?.lastObservation || props.isFormDisabled}
                                        style={{marginTop: 10, marginLeft: 10}}
                                        variant="contained"
                                    >
                                        {t('Copy From Observation Table')}
                                    </AgyleButton>}
                            </div>
                        </Grid>
                    }

                    <Grid item xs={12} sx={{mt: 2}}>
                        <AgyleAccordion
                            expanded={expanded.flags}
                            onChange={accordionChangeHandler('flags')}
                            label={t('Flags')}
                            formBackground
                        >
                            <Grid container spacing={2} alignItems="center">
                                {!isFlagDisabled('111') &&
                                    <>
                                        <Grid item xs={isSmall ? 12 : 4} className={classes.flagContainer}>
                                         <span className={classes.flagTitle}>
                                            {t(gpExpected ? 'GP Expected' : '111')}
                                        </span>
                                            <AgyleBlockSwitch
                                                disabled={!props.canEditTriage}
                                                value={flags?.flag111?.checked || false}
                                                onChange={(val, event) => handleChangesFlags(event, '111')}
                                            />
                                        </Grid>

                                        <Grid item xs={isSmall ? 12 : 8}>
                                            {flags?.flag111?.checked && (
                                                <AgyleDatePicker
                                                    disabled={!props.canEditTriage}
                                                    label={t('Date and Time')}
                                                    error={(flags?.flag111?.checked) && !flag111Date}
                                                    type="dateTime"
                                                    value={flag111Date}
                                                    onChange={event => handleChangesFlagsDate(event, '111')}
                                                />
                                            )}
                                        </Grid>
                                    </>
                                }

                                <Grid item xs={12}>
                                    <Divider/>
                                </Grid>

                                {!isFlagDisabled('fitToSit') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('Fit to Sit')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.fitToSit}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'fitToSit')}
                                        />
                                    </Grid>
                                }

                                {!isFlagDisabled('seeAndTreat') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('See and Treat')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.seeAndTreat}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'seeAndTreat')}
                                        />
                                    </Grid>
                                }

                                <Grid item xs={12}>
                                    <Divider/>
                                </Grid>

                                {!isFlagDisabled('inCar') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('In Car')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.inCar}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'inCar')}
                                        />
                                    </Grid>
                                }

                                {!isFlagDisabled('res') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('Resus')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.res}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'res')}
                                        />
                                    </Grid>
                                }

                                {!isFlagDisabled('maj') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('Major')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.maj}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'maj')}
                                        />
                                    </Grid>
                                }

                                {!isFlagDisabled('amb') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('ACA')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.amb}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'amb')}
                                        />
                                    </Grid>
                                }

                                {!isFlagDisabled('min') &&
                                    <Grid item xs={isSmall ? 12 : 6} className={classes.flagContainer}>
                                        <span className={classes.flagTitle}>
                                            {t('Minor')}
                                        </span>

                                        <AgyleBlockSwitch
                                            value={flags.min}
                                            disabled={!props.canEditTriage}
                                            onChange={(_, event) => handleChangesFlags(event, 'min')}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </AgyleAccordion>
                    </Grid>

                    {!Boolean(alternativeWorkStreamTriage) && renderInformationAccordion()}

                    {!triageTreatmentForm &&
                        <Grid item xs={12}>
                            <AgyleAccordion
                                expanded={expanded.investigation}
                                onChange={accordionChangeHandler('investigation')}
                                label={t('Examination & Investigations')}
                                formBackground
                            >
                                <Grid container spacing={2} style={{marginTop: 1}}>
                                    <Grid item xs={12}>
                                        <TextFieldDebounce
                                            disabled={!props.canEditTriage}
                                            style={{width: '100%'}}
                                            label={t('Triage Examination & Investigations Information')}
                                            multiline
                                            agyleRefresh
                                            onChange={event => {
                                                setTriageObject((pr) =>
                                                    ({
                                                        ...pr,
                                                        examination: event.target.value,
                                                    }));
                                            }}
                                            value={triageObject.examination}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <ControlInvestigations
                                            disabled={!props.canEditTriage}
                                            label={t('ECDS Triage Investigations')}
                                            value={triageObject.investigationsTriage || []}
                                            color={'primary'}
                                            agyleRefresh
                                            onChange={value => {
                                                const clone = _.cloneDeep(triageObject);
                                                clone.investigationsTriage = value;
                                                setTriageObject(clone);
                                            }}
                                            admitDate={props.patient?.admission?.startDate}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        {props.modeClerking &&
                                            <TextFieldDebounce
                                                disabled={!props.canEditTriage}
                                                style={{width: '100%'}}
                                                agyleRefresh
                                                label={t('Investigations Information')}
                                                onChange={event => {
                                                    setTriageObject((pr) =>
                                                        ({
                                                            ...pr,
                                                            investigationsDetails: event.target.value,
                                                        }));
                                                }}
                                                value={triageObject.investigationsDetails}
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />}
                                    </Grid>
                                </Grid>
                            </AgyleAccordion>
                        </Grid>}

                    {!triageTreatmentForm &&
                        <Grid item xs={12}>
                            <AgyleAccordion
                                expanded={expanded.managePlan}
                                onChange={accordionChangeHandler('managePlan')}
                                label={t('Treatment and management plan')}
                                formBackground
                            >
                                <Grid container spacing={2} style={{marginTop: 1}}>
                                    <Grid item xs={12}>
                                        <ControlTreatment
                                            disabled={!props.canEditTriage}
                                            label={t('ECDS Treatment and management plan')}
                                            value={triageObject.treatmentAndManagementPlanTriage || []}
                                            color="primary"
                                            agyleRefresh
                                            onChange={value => {
                                                const clone = _.cloneDeep(triageObject);
                                                clone.treatmentAndManagementPlanTriage = value;
                                                setTriageObject(clone);
                                            }}
                                            admitDate={props.patient?.admission?.startDate}
                                        />
                                    </Grid>

                                    {!triageTreatmentForm &&
                                        <Grid item xs={12}>
                                            <TextFieldDebounce
                                                disabled={!props.canEditTriage}
                                                style={{width: '100%'}}
                                                label={t('Triage Treatment and Management plan Information')}
                                                multiline
                                                agyleRefresh
                                                onChange={event => {
                                                    setTriageObject((pr) =>
                                                        ({
                                                            ...pr,
                                                            additionalInformation: event.target.value,
                                                        }));
                                                }}
                                                value={triageObject.additionalInformation}
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            </AgyleAccordion>
                        </Grid>}

                    <AgyleDialog
                        title={t('Observations Comparison')}
                        open={isOpenObsDialog}
                        onClose={closeObsDialog}
                        disableSave
                        width={'xl'}
                        buttons={[
                            <Stack direction="row" spacing={1} alignItems="center" sx={{mr: 3}}>
                                <Typography>Under</Typography>
                                <Switch checked={isOneByOne} onChange={handleObsModeChange}/>
                                <Typography>Side by side</Typography>
                            </Stack>,
                            <AgyleButton
                                variant="contained"
                                color="primary"
                                onClick={addObsAndClose}
                                className={classes.button}
                            >
                                {t('Add and copy to clipboard')}
                            </AgyleButton>,
                        ]}
                    >
                        <ObservationTable patient={props.patient} chooseObs chosenObs={chosenObs}
                                          setChosenObs={setChosenObs}/>
                    </AgyleDialog>

                    {(props.patientType !== PATIENT_AGE_TYPE.PAEDS && !alternativeWorkStreamTriage) &&
                        renderSafeguarding()
                    }
                </>
            }
        </>
    );
};

export {Triage};
